import { type FormEvent, useEffect, useRef, useState } from 'react';
import type { CostValue } from 'shared-models';
import type { ShiftCost } from '../../models/costs.types';
import { shiftWorkforceService } from '../../services/workforce/shift-workforce.service';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { today } from './costs-page.utils';

export function useCostsPageController() {
  const { user } = useShiftAuth();

  const [data, setData] = useState<Awaited<ReturnType<typeof shiftWorkforceService.getCosts>> | null>(null);
  const [typeCost, setTypeCost] = useState<'regular' | 'additional'>('additional');
  const [name, setName] = useState('');
  const [regularType, setRegularType] = useState<NonNullable<ShiftCost['type']>>('hourly');
  const [additionalCode, setAdditionalCode] =
    useState<NonNullable<ShiftCost['additionalCode']>>('restaurant_deduction');
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [valueCostId, setValueCostId] = useState<number | null>(null);
  const [value, setValue] = useState('');
  const [valueRoleId, setValueRoleId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const mutationLock = useRef(false);
  const pageLock = useRef(false);
  const generation = useRef(0);
  const nextOffset = useRef(0);
  const liveCosts = useRef(new Map<number, ShiftCost | null>());
  const liveValues = useRef(new Map<number, Map<number, CostValue | null>>());
  const liveShifts = useRef(new Map<number, { id: number; name: string; tipShiftType: 'morning' | 'evening' | null }>());

  const mergeValues = (previous: CostValue[], next: CostValue[]) =>
    [...new Map([...previous, ...next].map((value) => [value.id, value])).values()]
      .sort((left, right) => right.valueDateStart.localeCompare(left.valueDateStart) || right.id - left.id);

  const applyValueChanges = (cost: ShiftCost, changes?: Map<number, CostValue | null>) => ({ ...cost,
    values: cost.values.flatMap((value) => changes?.has(value.id) ? changes.get(value.id) ?? [] : value) });

  const mergeCosts = (costs: ShiftCost[], changes: Map<number, ShiftCost | null>) => {
    const merged = new Map(costs.map((cost) => [cost.id, cost]));
    for (const [id, cost] of changes) {
      if (cost) {
        const existing = merged.get(id);
        merged.set(id, existing ? { ...cost, values: mergeValues(existing.values, cost.values),
          valuesPagination: (existing.valuesPagination?.offset ?? 0) > 0 ? existing.valuesPagination : cost.valuesPagination } : cost);
      }
      else merged.delete(id);
    }
    return [...merged.values()].sort((left, right) => Number(right.isActive) - Number(left.isActive)
      || right.createdAt.localeCompare(left.createdAt) || right.id - left.id);
  };

  const loadCosts = async (append = false) => {
    if (append && pageLock.current) return;
    pageLock.current = true;
    const requestGeneration = ++generation.current;
    liveCosts.current.clear();
    liveValues.current.clear();
    liveShifts.current.clear();
    if (append) setIsLoadingMore(true);
    try {
      const offset = append ? nextOffset.current : 0;
      const next = await shiftWorkforceService.getCosts({ offset, limit: 50 });
      if (requestGeneration !== generation.current) return;
      nextOffset.current = offset + next.costs.length;
      const changes = new Map(liveCosts.current);
      const valueChanges = new Map(liveValues.current);
      const shifts = new Map(liveShifts.current);
      liveCosts.current.clear();
      liveValues.current.clear();
      liveShifts.current.clear();
      setData((current) => ({ ...next, costs: mergeCosts(append && current
        ? [...current.costs, ...next.costs.filter((row) => !current.costs.some((existing) => existing.id === row.id))]
        : next.costs, changes).map((cost) => applyValueChanges(cost, valueChanges.get(cost.id))),
        shifts: next.shifts.map((shift) => shifts.get(shift.id) ?? shift) }));
      setError(null);
    } catch {
      if (requestGeneration === generation.current) setError('לא ניתן לטעון את העלויות.');
    } finally {
      if (requestGeneration === generation.current) {
        pageLock.current = false;
        setIsLoadingMore(false);
      }
    }
  };

  const onLoadMoreValues = async (cost: ShiftCost) => {
    if (pageLock.current) return;
    pageLock.current = true;
    const requestGeneration = ++generation.current;
    liveValues.current.clear();
    setIsLoadingMore(true);
    try {
      const next = await shiftWorkforceService.getCostValues(cost.id, { offset: cost.values.length, limit: 50 });
      if (requestGeneration !== generation.current) return;
      const changes = new Map(liveValues.current.get(cost.id));
      setData((current) => current ? { ...current, costs: current.costs.map((row) => row.id === cost.id
        ? applyValueChanges({ ...row, values: mergeValues(next.values, row.values), valuesPagination: next.pagination }, changes)
        : row) } : current);
    } catch {
      if (requestGeneration === generation.current) setError('לא ניתן לטעון את היסטוריית העלות.');
    } finally {
      if (requestGeneration === generation.current) {
        liveCosts.current.clear();
        liveValues.current.clear();
        liveShifts.current.clear();
        pageLock.current = false;
        setIsLoadingMore(false);
      }
    }
  };

  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'cost.upserted' || event.type === 'cost.removed') {
      const id = event.type === 'cost.upserted' ? event.cost.id : event.id;
      const cost = event.type === 'cost.upserted' ? event.cost : null;
      if (pageLock.current) liveCosts.current.set(id, cost);
      setData((current) => current ? { ...current, costs: mergeCosts(current.costs, new Map([[id, cost]])) } : current);
    } else if (event.type === 'costValue.upserted' || event.type === 'costValue.removed') {
      const id = event.type === 'costValue.upserted' ? event.value.id : event.id;
      const value = event.type === 'costValue.upserted' ? event.value : null;
      if (pageLock.current) {
        const changes = liveValues.current.get(event.costId) ?? new Map<number, CostValue | null>();
        changes.set(id, value);
        liveValues.current.set(event.costId, changes);
      }
      setData((current) => current ? { ...current, costs: current.costs.map((cost) => cost.id === event.costId
        ? applyValueChanges(cost, new Map([[id, value]])) : cost) } : current);
    } else if (event.type === 'shift.tipClassificationChanged') {
      if (pageLock.current) liveShifts.current.set(event.shift.id, event.shift);
      setData((current) => current ? { ...current, shifts: current.shifts.map((shift) => shift.id === event.shift.id ? event.shift : shift) } : current);
    } else if (event.type === 'sse.resync') void loadCosts();
  });

  useEffect(() => {
    if (eventsReady && user?.role === 'manager') void loadCosts();
    return () => { generation.current += 1; };
  }, [user?.role, eventsReady]);

  const saveCostChange = async (action: () => Promise<unknown>) => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await action();
    } catch {
      setError('לא ניתן לשמור. בדקו שאין התנגשות בתפקיד או בטווח התאריכים.');
    } finally {
      mutationLock.current = false;
      setIsSaving(false);
    }
  };

  const onCreateCost = (event: FormEvent) => {
    event.preventDefault();
    void saveCostChange(async () => {
      await shiftWorkforceService.createCost({
        name,
        typeCost,
        type: typeCost === 'regular' ? regularType : null,
        additionalCode: typeCost === 'additional' ? additionalCode : null,
        roleIds: typeCost === 'regular' ? roleIds : [],
      });
      setName('');
      setRoleIds([]);
    });
  };

  const selectedCost = data?.costs.find((cost) => cost.id === valueCostId) ?? null;

  const onAddCostValue = (event: FormEvent) => {
    event.preventDefault();
    if (!valueCostId) return;
    void saveCostChange(async () => {
      await shiftWorkforceService.createCostValue(valueCostId, {
        startDate,
        endDate: endDate || null,
        value,
        roleId:
          selectedCost?.additionalCode === 'tip_grade' ||
          selectedCost?.additionalCode === 'minimum_completion'
            ? valueRoleId
            : null,
      });
      setValue('');
      setEndDate('');
    });
  };

  const onChangeShiftTipType = (id: number, value: string) =>
    saveCostChange(() =>
      shiftWorkforceService.setShiftTipType(id, (value || null) as 'morning' | 'evening' | null),
    );
  const onToggleCostActive = (id: number, isActive: boolean) =>
    saveCostChange(() => shiftWorkforceService.setCostActive(id, !isActive));

  return {
    user,
    data,
    typeCost,
    setTypeCost,
    name,
    setName,
    regularType,
    setRegularType,
    additionalCode,
    setAdditionalCode,
    roleIds,
    setRoleIds,
    valueCostId,
    setValueCostId,
    value,
    setValue,
    valueRoleId,
    setValueRoleId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    error,
    isSaving,
    isLoadingMore,
    onLoadMoreCosts: () => loadCosts(true),
    onLoadMoreValues,
    onCreateCost,
    onAddCostValue,
    selectedCost,
    onChangeShiftTipType,
    onToggleCostActive,
  };
}
